import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaRegCopy, FaTrash, FaEdit, FaDownload } from "react-icons/fa";
import axios from "axios";
import { fetchJsonFromBack, saveJsonLearnToBack, updateJsonDataLearn } from "../../../store/jsonSliceLearn.js";
import Title from "../../../components/Title.js";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button.js";
import TextInput from "../../../components/TextInput.js";
import TextArea from "../../../components/TextArea.js";
import { deleteFile, uploadFile, copyFile } from "../../../services/api/jsonService.ts";
import QuizzesManager, { addTmpIdToQuiz } from "../Action/quizzesManage.js";
import { convertToPng } from "../../../utils/imageUtils";
import HologramBlinkFields from "../../../components/HologramBlinkFields.js";
import { getDefaultHologramState, normalizeHologramState, validateHologramState } from "../../../utils/hologramState";
import { use } from "react";


const TopicsManage = (props) => {
  const routeParams = useParams();
  const navigate = useNavigate();
  const rootFolder = props.rootFolder || routeParams.rootFolder;
  const subFolder = props.subFolder || routeParams.subFolder;
  const filename = props.filename || routeParams.filename;
  const topicIndex = props.topicIndex || routeParams.topicIndex;
  const [parentStages, setParentStages] = useState([]);

  const dispatch = useDispatch();
  const detailsRef = useRef(null);
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // État pour le contenu du topic
  const [Contents, setContents] = useState(getEmptyTopic());
  const [editingTopicIndex, setEditingTopicIndex] = useState(null);

  // État pour les fichiers en attente
  const [pendingImg, setPendingImg] = useState(null);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [pendingHologram, setPendingHologram] = useState(null);
  const [pendingEnv360, setPendingEnv360] = useState(null);
  const [previewTopicImgUrl, setPreviewTopicImgUrl] = useState(null);
  const [previewEnv360Url, setPreviewEnv360Url] = useState(null);
  const [previewTopicAudioUrl, setPreviewTopicAudioUrl] = useState(null);

  // etat pour l'ouverture de la modal pour agrandir l'image
  const [isImgModalOpen, setIsImgModalOpen] = useState(false);
  const [imgModalSrc, setImgModalSrc] = useState(null);
  const openImgModal = (src) => {
    if (!src) return;
    setImgModalSrc(src);
    setIsImgModalOpen(true);
  };

  const closeImgModal = () => {
    setIsImgModalOpen(false);
    setImgModalSrc(null);
  };


  // Construction du chemin
  let chapterName = undefined;
  let languageFolder = undefined;
  let learnFilename = undefined;

  // Correction vignette : fallback pour folderPath si buildFilePaths retourne null
  let fallbackFolderPath = undefined;
  if (routeParams.folderName) {
    const cleanName = routeParams.folderName.replace(/\.json$/, '');
    const base = cleanName.replace(/_learn$/, '');
    const chapter = routeParams.chapterName || base.split('_')[0];
    fallbackFolderPath = `${chapter}/${base}/learn`;
  }

  // Récupérer l'état complet du store
  const {
    jsonData,
    loading: jsonLoading,
    error,
  } = useSelector((state) => ({
    jsonData: state.jsonLearn?.jsonData || {
      process: {
        topics: [],
        courses: [],
      },
    },
    loading: state.jsonLearn?.loading || false,
    error: state.jsonLearn?.error || null,
  }));

  // Initialiser les données avec des valeurs par défaut si nécessaire
  const process = jsonData?.process || {
    topics: [],
    courses: [],
  };

  // S'assurer que topics est un tableau
  const topics = Array.isArray(process.topics) ? process.topics : [];

  // Sécuriser topics pour éviter les erreurs
  const validTopics = Array.isArray(topics) ? topics.filter((s) => s && typeof s.topicID !== "undefined") : [];

  // ========================================
  // FONCTIONS UTILITAIRES CENTRALISÉES
  // ========================================

  /**
   * Construit les chemins de fichiers à partir du nom de dossier
   */
  const buildFilePaths = (folderName) => {
    if (!folderName) return null;

    const cleanName = folderName.replace(/\.json$/, '');
    const base = cleanName.replace(/_learn$/, '');
    const chapterName = routeParams.chapterName || base.split('_')[0];
    const languageFolder = `${base}/learn`;
    const filename = `${base}_learn.json`;
    const folderPath = `${chapterName}/${languageFolder}`;

    return {
      chapterName,
      languageFolder,
      filename,
      folderPath
    };
  };

  const selectedFolderName = routeParams.folderName || filename || null;
  const paths = buildFilePaths(selectedFolderName);
  const folderPath = paths?.folderPath;
  /**
   * Construit la structure JSON mise à jour
   */
  const buildUpdatedJson = (newTopics, courses = null) => ({
    usage: jsonData?.usage || "Fichier des questionnaires liés aux procédures (process) d'iXiLEARN.",
    settings: {
      contentVersion: jsonData?.settings?.contentVersion || 1.0,
      ownerName: jsonData?.settings?.ownerName || '',
      ownerIdentifier: jsonData?.settings?.ownerIdentifier || '',
      appVersion: jsonData?.settings?.appVersion || "",
      chapter: jsonData?.settings?.chapter || "",
      calibrateAfterStageID: jsonData?.settings?.calibrateAfterStageID || 0,
      unit: jsonData?.settings?.unit || "mm",
      langCode: jsonData?.settings?.langCode || ""
    },
    process: {
      topics: newTopics,
      courses: courses || (Array.isArray(jsonData?.process?.courses) ? jsonData.process.courses : [])
    }
  });

  /**
   * Sauvegarde les données des topics
   */
  const saveTopicsData = async (updatedTopics) => {
    const paths = buildFilePaths(routeParams.folderName);
    if (!paths) return;

    const updatedJson = buildUpdatedJson(updatedTopics);

    dispatch(updateJsonDataLearn(updatedJson));

    await dispatch(saveJsonLearnToBack({
      updatedData: updatedJson,
      chapterName: paths.chapterName,
      languageFolder: paths.languageFolder,
      filename: paths.filename
    }));
  };

  /**
   * Calcule le prochain topicID disponible
   */
  const getNextTopicID = () => {
    if (!validTopics || validTopics.length === 0) return 1;
    const existingIds = validTopics.map((s) => parseInt(s.topicID) || 0);
    let nextId = 1;
    while (existingIds.includes(nextId)) {
      nextId++;
    }
    return nextId;
  };

  /**
   * Retourne un topic vide
   */
  function getEmptyTopic() {
    return {
      topicID: 0,
      topicTitle: "",
      topicDesc: "",
      topicImg: "",
      topicHologram: "",
      linkedStageID: "0",
      topicEnv360: "",
      topicEnv360Rot: "",
      topicHologramOffsetPos: { x: 0, y: 0, z: 0 },
      topicHologramOffsetRot: { x: 0, y: 0, z: 0 },
      topicHologramInteractable: false,
      topicHologramState: getDefaultHologramState(),
      topicAudioText: "",
      topicAudio: "",
      quizzes: [],
    };
  }

  /**
   * Remet à zéro les états du formulaire
   */
  function resetFormStates() {
    setContents(getEmptyTopic());
    setEditingTopicIndex(null);
    setPendingImg(null);
    setPendingAudio(null);
    setPendingHologram(null);
    setPendingEnv360(null);
  }

  // fonction pour le défilement vers la zone de contenu
  const scrollToContentArea = (ElementId) => {
    setTimeout(() => {
      const anchor = document.getElementById(ElementId);
      if (anchor) anchor.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };
  // ========================================
  // EFFECTS
  // ========================================

  // Détecter le chemin du JSON parent (normal, pas _learn)
  useEffect(() => {
    let parentJsonPath = null;
    if (routeParams.folderName) {
      const cleanName = routeParams.folderName.replace(/\.json$/, '');
      const base = cleanName.replace(/_learn$/, '');
      const parentFolder = routeParams.chapterName || base.split('_')[0];

      parentJsonPath = `${parentFolder}/${base}/${base}.json`;
    } else if (filename && filename.endsWith('_learn.json')) {
      parentJsonPath = filename.replace(/_learn\.json$/, '.json');
      if (rootFolder) parentJsonPath = `${rootFolder}/${parentJsonPath}`;
    }
    if (!parentJsonPath) return;

    const url = `/api/data/${parentJsonPath}`;
    axios.get(url, { withCredentials: true })
      .then(res => {
        const json = res;

        if (json && json.process && Array.isArray(json.process.stages)) {
          setParentStages(json.process.stages);
        } else if (json && json.process && Array.isArray(json.process.stages)) {
          setParentStages(json.process.stages);
        } else {
          setParentStages([]);
        }
      })
      .catch(err => {
        setParentStages([]);
      });
  }, [routeParams.folderName, filename, rootFolder]);

  if (routeParams.folderName) {
    const cleanName = routeParams.folderName.replace(/\.json$/, '');
    const base = cleanName.replace(/_learn$/, '');
    chapterName = routeParams.chapterName || base.split('_')[0];
    languageFolder = `${base}/learn`;
    learnFilename = `${cleanName}.json`;
  } else if (filename && filename.endsWith('_learn.json')) {
    const base = filename.replace(/_learn\.json$/, '');
    chapterName = rootFolder;
    languageFolder = `${base}/learn`;
    learnFilename = `${base}_learn.json`;
  } else if (rootFolder && filename) {
    chapterName = rootFolder;
    languageFolder = filename.replace(/\.json$/, '');
    learnFilename = filename;
  } else if (rootFolder) {
    chapterName = rootFolder;
    languageFolder = undefined;
    learnFilename = undefined;
  }

  useEffect(() => {
    // N'assigner un ID automatiquement que si on est en train de créer (editingTopicIndex === null) 
    // et que le titre commence à être saisi, pour éviter de boucler sur un state vide.
    if (!Contents.topicID && editingTopicIndex === null && Contents.topicTitle) {
      const newId = getNextTopicID();
      setContents(prev => ({
        ...prev,
        topicID: newId
      }));
    }
  }, [Contents.topicID, Contents.topicTitle, editingTopicIndex, validTopics]);

  // Chargement initial
  useEffect(() => {
    if (routeParams.folderName) {
      const paths = buildFilePaths(routeParams.folderName);
      if (paths) {
        console.log("Fetching JSON from backend for:", paths.filename);
        dispatch(fetchJsonFromBack({
          folderName: paths.folderPath,
          filename: paths.filename
        }));
      }
    }
  }, [routeParams.folderName, dispatch]);

  const [hasFetched, setHasFetched] = useState(false);
  const parentFolder = window.location.pathname.split("/")[2];
  const jsonFilename = learnFilename ? `${languageFolder}/${learnFilename}` : `${languageFolder}.json`;
  const isEditing = topicIndex && !isNaN(Number(topicIndex));

  useEffect(() => {
    if (isEditing && topics.length === 0 && !hasFetched) {
      setLoading(true);
      console.log("2 Fetching JSON from backend for:", jsonFilename);
      dispatch(fetchJsonFromBack({ parentFolder, jsonFilename })).finally(
        () => {
          setLoading(false);
          setHasFetched(true);
        }
      );
    }
  }, [
    isEditing,
    topics.length,
    dispatch,
    parentFolder,
    jsonFilename,
  ]);

  // Vérifier si on est en mode édition et charger les données du topic
  useEffect(() => {
    if (isEditing && topics.length > 0) {
      const existing = topics.find((s) => s.topicID === parseInt(topicIndex, 10));
      if (existing) {
        const quizzesWithTmpId = (existing.quizzes || []).map(addTmpIdToQuiz);
        setContents({ ...existing, quizzes: quizzesWithTmpId });
      } else {
        // L'ID en cours d'édition n'existe plus (supprimé ou ré-indexé). On nettoie l'URL.
        const cleanName = routeParams.folderName ? routeParams.folderName : (filename || "");
        const cleanChapter = routeParams.chapterName || rootFolder;
        navigate(`/homemonoLearn/${encodeURIComponent(cleanChapter)}/${encodeURIComponent(cleanName)}`);
      }
    }
  }, [isEditing, topicIndex, topics, navigate, routeParams, filename, rootFolder]);

  // Live preview for Topic Image
  useEffect(() => {
    if (!pendingImg) {
      setPreviewTopicImgUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingImg);
    setPreviewTopicImgUrl(url);
    console.log('Preview Topic Image URL:', url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImg]);

  // Live preview for Image 360
  useEffect(() => {
    if (!pendingEnv360) {
      setPreviewEnv360Url(null);
      return;
    }
    const url = URL.createObjectURL(pendingEnv360);
    setPreviewEnv360Url(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingEnv360]);

  // live preview for Topic Audio
  useEffect(() => {
    if (!pendingAudio) {
      setPreviewTopicAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingAudio);
    setPreviewTopicAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingAudio]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleDeleteFile = async ({
    fileName,
    params,
    ext,
    fieldName,
  }) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir SUPPRIMER DEFINITIVEMENT ce fichier ?")) return;
    if (!fileName || !params) {
      return;
    }
    try {
      const paths = buildFilePaths(routeParams.folderName);
      if (!paths) return;

      const data = {
        folder: paths.folderPath,
        suffix: fileName,
        defaultExt: ext,
        params,
      };

      await deleteFile(data);
      setContents(prev => ({
        ...prev,
        [fieldName]: "",
        // Sans hologramme, le clignotement repasse à l'état par défaut (Off, vitesse 1)
        ...(fieldName === "topicHologram" && { topicHologramState: getDefaultHologramState() })
      }));
      setMessage({ type: "success", text: ` supprimé avec succès !` });
    } catch (err) {
      setMessage({ type: "error", text: `Erreur lors de la suppression du fichier.` });
    }
  };

  const handleEditTopic = (topic) => {
    const idx = topics.findIndex((t) => t.topicID === topic.topicID);
    setEditingTopicIndex(idx);
    setContents({ ...topic });
  };

  const handleUploadFile = async ({
    file,
    params,
    fieldName,
    suffix,
    defaultExt,
  }) => {
    if (!file || !params) {
      setMessage && setMessage({ type: "error", text: "Paramètres manquants pour l'upload." });
      return;
    }
    try {
      const paths = buildFilePaths(routeParams.folderName);
      if (!paths) return;

      const data = {
        file,
        folder: paths.folderPath,
        suffix,
        defaultExt,
        params
      };

      const res = await uploadFile(data);

      const fileName = params.length > 1
        ? `topic-${params[0]}_quiz-${params[1]}_${suffix}${defaultExt}`
        : `topic-${params[0]}_${suffix}${defaultExt}`;
      // Ne mettre à jour Contents que pour les champs du topic
      if (!fieldName.startsWith("quiz")) {
        setContents(prev => ({
          ...prev,
          [fieldName]: fileName
        }));
      }
      setFile(null);

      return fileName;
    } catch (err) {
      console.error('[UPLOAD] Erreur', err);
      setMessage && setMessage({ type: "error", text: "Erreur lors de l'upload du fichier." });
      return null;
    }
  };

  const handleDownloadHologram = async (fileName) => {
    try {
      const response = await axios.get(`/api/upload/download-hologram/${folderPath}`, {
        params: { 
          fileName,
          t: new Date().getTime()
        },
        responseType: 'blob',
        withCredentials: true
      });

      // L'intercepteur axios (api_helper.js) renvoie déjà response.data : "response" est directement le Blob
      const blob = response;

      if (blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        console.error('❌ ERREUR MASQUÉE : Le serveur a renvoyé du JSON au lieu d\'un fichier :', text);
        alert('Erreur serveur : ' + text);
        return;
      }

      // 2. Vérification critique de la taille
      if (blob.size === 0) {
        console.error('❌ ERREUR : Le Blob reçu pèse 0 octet !');
        alert('Le fichier téléchargé est vide.');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'hologram.glb');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors du téléchargement du fichier.');
      console.error(err);
    }
  };

  // Fonction pour gérer les changements de champs
  const handleChange = (field, value) => {
    if (typeof value === 'object') {
      console.log(`Debug OnChange: ${field}`, JSON.stringify(value));
    } else {
      console.log(`Debug OnChange: ${field}`, value);
    }
    setContents((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    console.log("submit");
    e.preventDefault();

    // Vitesse de clignotement de l'hologramme (avant tout upload)
    if (pendingHologram || Contents.topicHologram) {
      const holoCheck = validateHologramState(Contents.topicHologramState);
      if (!holoCheck.isValid) {
        setMessage({ type: "error", text: holoCheck.error });
        return;
      }
    }
    setLoading(true);

    // Générer l'ID si inexistant
    const currentTopicID = Contents.topicID || getNextTopicID();

    // Upload des fichiers et mise à jour des champs correspondants
    if (pendingImg) {
      const imgFile = await handleUploadFile({
        file: pendingImg,
        params: [currentTopicID],
        fieldName: "topicImg",
        suffix: "img",
        defaultExt: ".png"
      });
      if (imgFile) Contents.topicImg = imgFile;
    }

    if (pendingAudio) {
      const audioFile = await handleUploadFile({
        file: pendingAudio,
        params: [currentTopicID],
        fieldName: "topicAudio",
        suffix: "audio",
        defaultExt: ".ogg"
      });
      if (audioFile) Contents.topicAudio = audioFile;
    }

    if (pendingHologram) {
      const hologramFile = await handleUploadFile({
        file: pendingHologram,
        params: [currentTopicID],
        fieldName: "topicHologram",
        suffix: "hologram",
        defaultExt: ".glb"
      });
      if (hologramFile) Contents.topicHologram = hologramFile;
    }

    if (pendingEnv360) {
      const topicEnv360 = await handleUploadFile({
        file: pendingEnv360,
        params: [currentTopicID],
        suffix: "env",
        fieldName: "topicEnv360",
        defaultExt: ".jpg",
        fileType: "topic"
      });
      if (topicEnv360) Contents.topicEnv360 = topicEnv360;
    }

    // Upload des fichiers des questions (image / audio en attente)
    let copyErrors = 0;
    if (Array.isArray(Contents.quizzes)) {
      Contents.quizzes = await Promise.all(Contents.quizzes.map(async (quiz, index) => {
        const { _copyFrom, ...quizWithoutCopy } = quiz;
        const updatedQuiz = {
          ...quizWithoutCopy,
          // Questions anciennes ou collées : le sous-objet est toujours écrit (copie indépendante)
          quizHologramState: normalizeHologramState(quizWithoutCopy.quizHologramState),
        };

        // Question collée/dupliquée : on duplique ses fichiers sous le nom de sa nouvelle position
        // (sauf si le fichier a été remplacé ou supprimé entre-temps)
        if (_copyFrom) {
          const suffixes = { quizImg: "img", quizAudio: "audio", quizHologram: "hologram" };
          for (const [field, sourceName] of Object.entries(_copyFrom)) {
            if (quiz[field] !== sourceName) continue;
            const ext = sourceName.match(/\.[^.]+$/)?.[0] || "";
            const targetName = `topic-${currentTopicID}_quiz-${index + 1}_${suffixes[field]}${ext}`;
            try {
              await copyFile({ folder: buildFilePaths(routeParams.folderName).folderPath, from: sourceName, to: targetName });
              updatedQuiz[field] = targetName;
            } catch (err) {
              console.error(`[COPY] ${field} de la question "${quiz.quizQuestion}"`, err);
              updatedQuiz[field] = "";
              copyErrors++;
            }
          }
        }

        if (quiz.quizImg instanceof Blob) {
          const imgFileName = await handleUploadFile({
            file: quiz.quizImg,
            params: [currentTopicID, index + 1],
            fieldName: "quizImg",
            suffix: "img",
            defaultExt: ".png"
          });
          updatedQuiz.quizImg = imgFileName || "";
        }

        if (quiz.quizHologram instanceof Blob) {
          const holoFileName = await handleUploadFile({
            file: quiz.quizHologram,
            params: [currentTopicID, index + 1],
            fieldName: "quizHologram",
            suffix: "hologram",
            defaultExt: ".glb"
          });
          updatedQuiz.quizHologram = holoFileName || "";
        }

        if (quiz.quizAudio instanceof Blob) {
          const audioFileName = await handleUploadFile({
            file: quiz.quizAudio,
            params: [currentTopicID, index + 1],
            fieldName: "quizAudio",
            suffix: "audio",
            defaultExt: ".ogg"
          });
          updatedQuiz.quizAudio = audioFileName || "";
        }

        return updatedQuiz;
      }));
    }

    // Conversion et validation des valeurs position/rotation
    const parseOrError = (val) => {
      if (typeof val !== 'string') return 0;
      const num = parseFloat(val.replace(',', '.'));
      return isNaN(num) ? null : num;
    };
    const posX = parseOrError(Contents.topicHologramOffsetPos.x);
    const posY = parseOrError(Contents.topicHologramOffsetPos.y);
    const posZ = parseOrError(Contents.topicHologramOffsetPos.z);
    const rotX = parseOrError(Contents.topicHologramOffsetRot.x);
    const rotY = parseOrError(Contents.topicHologramOffsetRot.y);
    const rotZ = parseOrError(Contents.topicHologramOffsetRot.z);

    if ([posX, posY, posZ, rotX, rotY, rotZ].includes(null)) {
      setMessage({
        type: "error",
        text: "Veuillez saisir des valeurs numériques valides pour la position et la rotation de l'hologramme."
      });
      setLoading(false);
      return;
    }

    try {
      // Préparation des topics mis à jour
      const updatedTopics = Array.isArray(jsonData?.process?.topics) ? [...jsonData.process.topics] : [];

      const topicHologramState = normalizeHologramState(Contents.topicHologramState);
      if (editingTopicIndex !== null && updatedTopics[editingTopicIndex]) {
        // Modification
        updatedTopics[editingTopicIndex] = { ...Contents, topicHologramState };
      } else {
        // Ajout
        updatedTopics.push({ ...Contents, topicHologramState, topicID: currentTopicID });
      }

      // Sauvegarde
      await saveTopicsData(updatedTopics);

      // Reset du formulaire et succès
      resetFormStates();
      setMessage(copyErrors > 0
        ? { type: "error", text: `Questionnaire enregistré, mais ${copyErrors} fichier(s) de question copiée(s) introuvable(s) : à ré-importer.` }
        : { type: "success", text: "Sujet enregistré avec succès" });

      // Scroll vers la liste
      scrollToContentArea("topicsList");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du sujet:", error);
      setMessage({ type: "error", text: "Erreur lors de la sauvegarde du sujet" });
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour annuler l'édition
  const handleCancel = () => {
    resetFormStates();
    console.log("cancel");
    scrollToContentArea("topicsList");
    /*setTimeout(() => {
      const anchor = document.getElementById("topicsList");
      if (anchor) anchor.scrollIntoView({ behavior: "smooth" });
    }, 100);*/
    detailsRef.current?.removeAttribute("open");
  };

  // Fonction pour dupliquer un topic
  const handleDuplicateTopic = async (topicToDuplicate) => {
    const nextTopicID = getNextTopicID();
    const duplicatedtopic = {
      ...topicToDuplicate,
      topicID: nextTopicID,
      topicTitle: `${topicToDuplicate.topicTitle} (copie)`,
    };

    const newTopics = [...topics, duplicatedtopic];
    await saveTopicsData(newTopics);
  };

  // Fonction pour supprimer un topic
  const handleDeleteTopic = async (topicID) => {
    console.log("=== [DEBUG] Start handleDeleteTopic for topicID:", topicID);
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir SUPPRIMER DEFINITIVEMENT ce questionnaire ?")) {
      console.log("=== [DEBUG] Deletion cancelled by user");
      return;
    }

    try {
      console.log("=== [DEBUG] Filtering topics. Total before:", topics.length);
      const filteredTopics = topics.filter((topic) => topic.topicID !== topicID);
      
      console.log("=== [DEBUG] Re-indexing remaining topics...");
      const newTopics = filteredTopics.map((topic, index) => ({
        ...topic,
        topicID: index + 1,
      }));
      console.log("=== [DEBUG] Re-indexed topics:", newTopics);

      console.log("=== [DEBUG] Updating courses...");
      const updatedCourses = (jsonData.process?.courses || []).map((course) => {
        if (!course.coursePath) return course;

        const newPath = course.coursePath
          .split('.')
          .map(Number)
          .filter((id) => id !== topicID)
          .map((id) => {
            if (id > topicID) {
              return id - 1;
            }
            return id;
          })
          .join('.');

        return {
          ...course,
          coursePath: newPath,
        };
      });
      console.log("=== [DEBUG] Updated courses:", updatedCourses);

      const paths = buildFilePaths(routeParams.folderName);
      console.log("=== [DEBUG] Calculated paths:", paths);
      if (paths) {
        const updatedJson = buildUpdatedJson(newTopics, updatedCourses);
        console.log("=== [DEBUG] Dispatched updateJsonDataLearn with updatedJson:", updatedJson);
        dispatch(updateJsonDataLearn(updatedJson));
        
        console.log("=== [DEBUG] Saving to backend (async)...");
        dispatch(saveJsonLearnToBack({
          updatedData: updatedJson,
          chapterName: paths.chapterName,
          languageFolder: paths.languageFolder,
          filename: paths.filename
        }));
      }
    } catch (e) {
      console.error("=== [DEBUG ERROR] Error in handleDeleteTopic:", e);
    }

    console.log("=== [DEBUG] Calling resetFormStates() (synchrone)");
    resetFormStates();
    console.log("=== [DEBUG] End handleDeleteTopic");
  };

  // ========================================
  // RENDER CONDITIONS
  // ========================================

  const isReady =
    !isEditing ||
    (topics.length > 0 &&
      topics.some((s) => s.topicID === parseInt(topicIndex, 10)));

  if (!isReady) {
    return <div>Chargement des données…</div>;
  }

  // Affichage d'un loader si édition et topics non prêts
  if (isEditing && (loading || topics.length === 0)) {
    return <div>Chargement des données…</div>;
  }

  // ========================================
  // RENDER
  // ========================================

  return (
    <div alt="message" className="form-container p-4">
      {message && (
        <div
          className={`alert alert-${message.type === "success" ? "success" : "danger"} alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/************************** TOPICS ******************* */}
      {/*------------ LISTE DES SUJETS--------------*/}
      <a id="topicsList" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
      <h3 className="text-primary text-center">Liste des questionnaires</h3>
      <div alt="Liste des sujets" className="card shadow-sm mb-4 border border-primary" style={{ maxHeight: "320px", overflowY: "scroll" }}>
        <table className="table">
          <thead className="table-light">
            <tr>
              <th className="text-tertiary">ID</th>
              <th className="text-tertiary">Titre</th>
              <th className="text-tertiary">Description</th>
              <th className="text-tertiary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {topics
              .filter(
                (topic) => topic && typeof topic.topicID !== "undefined"
              )
              .map((topic) => (
                <tr key={topic.topicID}>
                  <td>{topic.topicID}</td>
                  <td alt="Titre du sujet"
                    style={{
                      width: "200px",
                      cursor: editingTopicIndex !== null ? "not-allowed" : "pointer"
                    }}
                    className="fw-bold text-primary"
                    //onClick : si editingTopicIndex est null, alors on peut éditer
                    onClick={() => {
                      if (editingTopicIndex === null) {
                        handleEditTopic(topic);
                      }
                    }}
                    title={editingTopicIndex !== null
                      ? "Déjà en édition\nVeuillez annuler ou de sauvegarder"
                      : "Modifier ce questionnaire"}
                  >
                    {topic.topicTitle}
                  </td>
                  <td>{topic.topicDesc}</td>
                  {editingTopicIndex == null ? (
                    <td style={{ width: "140px" }}>
                      <button alt="Modifier le sujet"
                        type="button"
                        title="Modifier le questionnaire"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          const idx = topics.findIndex(
                            (s) => s.topicID === topic.topicID
                          );
                          setEditingTopicIndex(idx);
                          setContents({ ...topic });
                          scrollToContentArea("topicEdit");
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button alt="Dupliquer le sujet"
                        type="button"
                        title="Dupliquer le questionnaire"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => handleDuplicateTopic(topic)}
                      >
                        <FaRegCopy />
                      </button>
                      <button alt="Supprimer le topic"
                        type="button"
                        title="❌Supprimer le questionnaire"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteTopic(topic.topicID)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  ) : (
                    <td style={{ width: "140px" }}>
                      <button alt="disabled"
                        type="button"
                        title="Cliquez pour annuler les modifications"
                        //style={{ cursor: editingTopicIndex !== null ? "not-allowed" : "pointer" }}
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleCancel}
                      >
                        Edition en cours
                      </button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ------------- AJOUT / MODIFICATION SUJET -----------*/}
      <div className="mb-4">
        <a id="topicEdit" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
        <Title alt="Titre modifier / créer"
          text={
            editingTopicIndex !== null
              ? `Modifier le Questionnaire${Contents.topicTitle ? ' "' + Contents.topicTitle + '"' : ''}`
              : "Créer un Questionnaire"
          }
          className="text-center text-primary"
        />
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div alt="Topic title" className="col-md-6" title="Nom du questionnaire traité, 
            tel qu'il sera affiché dans l'interface utilisateur.">
              <TextInput
                label="Titre du Questionnaire ℹ️"
                placeholder="Ex : Maintenance préventive"
                value={Contents.topicTitle}
                onChange={(e) => handleChange("topicTitle", e.target.value)}
              />
            </div>
            <div alt="Environnement 360" className="col-md-3" title="Environnement par défaut du sujet (optionnel),
              Si vide, l'utilisateur verra son environnement réel.">
              <label><i className="mdi mdi-panorama-horizontal" /> Image 360° (jpg) ℹ️</label>
              {/* Always show input; show preview when selected, else existing */}
              <div className="d-flex flex-column align-items-start">
                <input alt="upload/replace file"
                  type="file"
                  accept=".jpg"
                  className="form-control"
                  title='📥 Choisir un fichier'
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPendingEnv360(file);
                    }
                  }}
                />
                {previewEnv360Url ? (
                  <img
                    src={previewEnv360Url}
                    alt="Vignette"
                    style={{
                      maxWidth: "60px",
                      maxHeight: "60px",
                      marginTop: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      cursor: "zoom-in"
                    }}
                    onClick={() => openImgModal(previewEnv360Url)}
                  />

                ) : (
                  <>
                    {Contents.topicEnv360 && (
                      <div alt="delete file" className="mt-2">
                        <img
                          src={`/api/data/${folderPath}/${Contents.topicEnv360}`}
                          alt="Vignette"
                          style={{
                            maxWidth: "60px",
                            maxHeight: "60px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            cursor: "zoom-in"
                          }}
                          onClick={() =>
                            openImgModal(`/api/data/${folderPath}/${Contents.topicEnv360}`)
                          }
                        />

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => handleDeleteFile({ fileName: "env", params: String([Contents.topicID]), ext: ".jpg", fieldName: "topicEnv360" })}
                          title={`❌ Supprimer le fichier existant (${Contents.topicEnv360})`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
            <div alt="RotEnv360x" className="col-md-2" title="Rotation de l'environnement 360° (en degrés).">
              <TextInput
                label="Rot° ℹ️"
                type="text"
                placeholder="0"
                value={Contents.topicEnv360Rot}
                onChange={e => {
                  const val = e.target.value;
                  if (/^-?[0-9]*[.,]?[0-9]*$/.test(val) || val === '') {
                    handleChange("topicEnv360Rot", val);
                    //handleChange("topicHologramOffsetRot", { ...Contents.topicHologramOffsetRot, x: val });
                  }
                }}
              />
            </div>
          </div>
          <div className="row">
            <div alt="Description du topic" className="col-md-6 mb-3" title="Description du sujet,
            tel qu'il sera affiché dans l'interface utilisateur.">
              <TextArea
                label="Description ℹ️"
                placeholder="Détails du sujet traité"
                value={Contents.topicDesc}
                onChange={(e) => handleChange("topicDesc", e.target.value)}
                rows={3}
              />
            </div>
            <div alt="audio texte du topic" className="col-md-6 mb-3" title="Texte à convertir en commentaire audio.
            Fournit à l'utilisateur des informations complémentaires
            sur le sujet en cours.">
              <TextArea
                label="Texte audio ℹ️"
                placeholder="Texte à lire"
                value={Contents.topicAudioText}
                onChange={(e) =>
                  handleChange("topicAudioText", e.target.value)
                }
                rows={2}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div alt="Image du sujet" className="col-md-3" title="Image du topic,
            affichée en dessous de la description dans l'interface utilisateur">
              <label><i className="mdi mdi-image-frame" /> Image du sujet (png/jpg) ℹ️</label>
              {/* Always show input; show preview when selected, else existing */}
              <div className="d-flex flex-column align-items-start">
                <input alt="upload/replace file"
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  className="form-control"
                  title='📥 Choisir un fichier'
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const pngFile = await convertToPng(file);
                      setPendingImg(pngFile);
                    }
                  }}
                />
                {previewTopicImgUrl ? (
                  <img
                    src={previewTopicImgUrl}
                    alt="Vignette"
                    style={{
                      maxWidth: "60px",
                      maxHeight: "60px",
                      marginTop: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      cursor: "zoom-in"
                    }}
                    onClick={() => openImgModal(previewTopicImgUrl)}
                  />

                ) : (
                  <>
                    {Contents.topicImg && (
                      <div alt="delete file" className="mt-2">
                        <img
                          src={folderPath && Contents.topicImg ? `/api/data/${folderPath}/${Contents.topicImg}` : ""}
                          alt="Vignette"
                          style={{
                            maxWidth: "60px",
                            maxHeight: "60px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            cursor: "zoom-in"
                          }}
                          onClick={() =>
                            openImgModal(`/api/data/${folderPath}/${Contents.topicImg}`)
                          }
                        />

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => handleDeleteFile({ fileName: "img", params: [Contents.topicID], ext: ".png", fieldName: "topicImg" })}
                          title={`❌ Supprimer le fichier existant (${Contents.topicImg})`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div alt="TopicHologram" className="col-md-3" title="Modèle 3D spécifique au questionnaire traité (format .glb).
             👉 Il remplace momentanément le modèle 3D principal de la procédure.
             (Par exemple, pour montrer un objet partiellement démonté.)">
              <label><i className="mdi mdi-cube-outline" /> Hologramme secondaire (glb) ℹ️</label>
              {Contents.topicHologram ? (
                <div alt="Secondary hologram" className="d-flex align-items-center">
                  <input alt="replace file"
                    type="file"
                    accept=".glb"
                    className="form-control"
                    style={{ width: "140px" }}
                    title='♻️ Remplacer le fichier existant'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingHologram(file);
                      }
                    }}
                  />
                  <div alt="delete file">
                    <span className="text-primary small fw-bold ms-2">[Fichier existant]</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success ms-2"
                      onClick={() => handleDownloadHologram(Contents.topicHologram)}
                      title={`⬇️ Télécharger le fichier\n(${Contents.topicHologram})`}
                    >
                      <FaDownload />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => handleDeleteFile({
                        fileName: "hologram",
                        params: [Contents.topicID],
                        ext: ".glb",
                        fieldName: "topicHologram"
                      })} title="❌ Supprimer le fichier existant"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : (
                <input alt="replace file"
                  type="file"
                  accept=".glb"
                  className="form-control"
                  title='📥 Ajouter un fichier'
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPendingHologram(file);
                    }
                  }}
                />
              )}
              {/* interactable / non-interactable */}
              {(!!pendingHologram || !!Contents.topicHologram) && (
                <div alt="gestion de l'hologramme" className="row">
                  <div alt="interactable" className="col-md-8">
                    <select
                      className="form-select form-select-sm mt-2"
                      value={Contents.topicHologramInteractable ? "true" : "false"}
                      onChange={(e) => handleChange("topicHologramInteractable", e.target.value === "true")}
                    >
                      <option value="false" title="L'hologramme remplace temporairement l'hologramme principal
                      (ex : un ordinateur sans sa plaque de fond.)">Hologramme fixe</option>
                      <option value="true" title="L'hologramme est manipulable par l'utilisateur.
                      Dédié aux petits objets sur lequel portent les actions
                      (ex : un disque sur lequel on doit fixer un chassis)">Hologramme manipulable</option>
                    </select>
                  </div>
                  {!Contents.topicHologramInteractable && (
                    <div alt="pos/rot" className="col-md-4 mt-3"/*className="d-flex align-items-center justify-content-start"*/>
                      <span className="me-2 small text-secondary" title="position X,Y,Z">
                        [{Contents.topicHologramOffsetPos?.x || 0},{Contents.topicHologramOffsetPos?.y || 0},{Contents.topicHologramOffsetPos?.z || 0}]
                      </span>
                      <span className="me-2 small text-secondary" title="rotation X,Y,Z">
                        [{Contents.topicHologramOffsetRot?.x || 0},{Contents.topicHologramOffsetRot?.y || 0},{Contents.topicHologramOffsetRot?.z || 0}]
                      </span>
                    </div>
                  )}
                </div>
              )}
              {(!!pendingHologram || !!Contents.topicHologram) && (
                <HologramBlinkFields
                  value={Contents.topicHologramState}
                  onChange={(state) => handleChange("topicHologramState", state)}
                />
              )}

            </div>
            <div className="col-md-3" alt="Fichier Audio" title="Fichier audio du commentaire.
            importé ici ou généré à partir du texte audio" >
              <label className="form-label"><i className="mdi mdi-volume-high" /> Audio (ogg) ℹ️</label>
              <input
                type="file"
                accept=".ogg"
                className="form-control"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) setPendingAudio(file);
                }}
              />
              {previewTopicAudioUrl ? (
                <audio
                  controls
                  src={previewTopicAudioUrl}
                  style={{ maxWidth: "calc(100% - 40px)", marginTop: "6px" }}
                  onLoadedData={e => console.log('Audio URL:', e.target.src)}
                >
                  Votre navigateur ne supporte pas l'audio.
                </audio>
              ) : (
                !!Contents.topicAudio && (
                  <div style={{ marginTop: '-2px' }}>
                    {/*<span className="text-secondary small fw-bold">Fichier actuel : {Contents.topicAudio}</span>*/}
                    <audio
                      controls
                      src={folderPath && Contents.topicAudio ? `/api/data/${folderPath}/${Contents.topicAudio}` : ""}
                      style={{ maxWidth: "calc(100% - 40px)" }}
                      onLoadedData={e => console.log('Audio URL:', e.target.src)}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                    <button
                      type="button"
                      title={`❌SUPPRIMER l'audio (${Contents.topicAudio})`}
                      style={{ marginTop: "-20px" }}
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => handleDeleteFile({ fileName: "audio", params: [Contents.topicID], ext: ".ogg", fieldName: "topicAudio" })}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )
              )}
            </div>
            <div className="col-md-3" alt="Procédure requise" title="Quelle procédure est requise
            avant de commencer les questions ?">
              <label>Procédure iXiWORK requise ℹ️</label>
              <select
                className="form-select"
                value={Contents.linkedStageID}
                onChange={e => handleChange("linkedStageID", e.target.value)}
              >
                <option value="">Aucun</option>
                {parentStages.map(stage => (
                  <option key={stage.stageID} value={stage.stageID}>
                    {stage.stageTitle || `Stage ${stage.stageID}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Composant ActionManager pour gérer toutes les actions */}
          <QuizzesManager
            Contents={Contents}
            setContents={setContents}
            editingTopicIndex={editingTopicIndex}
            folderName={folderPath}
            getNextTopicID={getNextTopicID}
            setMessage={setMessage}
            onUpload={handleUploadFile}
            onDelete={handleDeleteFile}
            detailsRef={detailsRef}
          />

          {/* ------------ BOUTON GESTION DU SUJET/TOPIC ------------- */}
          <div alt="Gestion du sujet/topic" id="validate" className="d-flex justify-content-center gap-3 mt-4">
            <Button
              type="button"
              text={editingTopicIndex !== null ? "Annuler les modifications" : "Annuler"}
              className="btn btn-lg btn-outline-secondary"
              onClick={handleCancel}
            />
            <Button
              text={
                !Contents.topicTitle
                  ? "⚠️ Titre du questionnaire manquant ⚠️"
                  : editingTopicIndex !== null
                    ? `Sauvegarder le Questionnaire "${Contents.topicTitle}"`
                    : `Créer le Questionnaire "${Contents.topicTitle}"`
              }
              type="submit"
              className="btn btn-lg btn-outline-primary"
              disabled={
                Contents.topicTitle.trim() === ""
              }
            />
          </div>
        </form>
      </div>
      {isImgModalOpen && (
        <div
          onClick={closeImgModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out"
          }}
        >
          <img
            src={imgModalSrc}
            alt="Aperçu grand format"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 30px rgba(0,0,0,0.6)"
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </div>
  );
};

export default TopicsManage;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license